import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

/**
 * Handles drag-and-drop reordering of filler items within a scene group.
 * Each scene's fillers are wrapped in a container with this controller.
 *
 * Values:
 *   url: String — endpoint for PATCH reorder_fillers
 *   sceneId: String — the source scene ID these fillers belong to
 */
export default class extends Controller {
  static targets = ["list"]
  static values = { url: String, sceneId: String }

  connect() {
    if (!this.hasListTarget) return

    this.sortable = Sortable.create(this.listTarget, {
      handle: "[data-filler-handle]",
      animation: 150,
      ghostClass: "opacity-30",
      dragClass: "shadow-lg",
      onEnd: this.onDragEnd.bind(this)
    })
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  async onDragEnd(event) {
    if (event.oldIndex === event.newIndex) return

    const items = this.listTarget.querySelectorAll("[data-filler-id]")
    const fillerIds = Array.from(items).map(el => el.dataset.fillerId)

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          scene_id: this.sceneIdValue,
          filler_ids: fillerIds
        })
      })

      if (!response.ok) {
        this.revertOrder(event)
      }
    } catch (error) {
      console.error("Filler reorder failed:", error)
      this.revertOrder(event)
    }
  }

  revertOrder(event) {
    const { oldIndex, newIndex } = event
    const parent = this.listTarget
    const children = Array.from(parent.children)
    const movedEl = children[newIndex]

    parent.removeChild(movedEl)

    if (oldIndex >= parent.children.length) {
      parent.appendChild(movedEl)
    } else {
      parent.insertBefore(movedEl, parent.children[oldIndex])
    }
  }
}
